import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Row } from 'antd';
import { maxBy } from 'es-toolkit';

import { getLocalDateTime } from '@/utils/date';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import { useGetOCClaimNotes, useOCClaimScrollPagination } from '@/views/screens/Claims/OverCharge/hooks';

import { routeFileSelector } from '../../../../../../../../utilz/i18n';
import Button from '../../../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../../../components/Labels/PrimaryLabel';
import OverChargeNoteCard from './OverChargeNoteCard';

const getNoteKey = (note) => note.sequenceNo;

const OverChargeNotes = forwardRef(({ claimDetails }, ref) => {
  const fbOcKey = claimDetails?.fbOcKey;
  const {
    data: claimNotesQueryData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isSuccess,
  } = useGetOCClaimNotes({ fbOcKey });

  const claimNotes = useMemo(
    () => ({
      data: claimNotesQueryData?.notes ?? [],
      totalRows: claimNotesQueryData?.totalRows ?? 0,
    }),
    [claimNotesQueryData],
  );

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [notesList, setNotesList] = useState([]);
  const [dirtyNoteKeys, setDirtyNoteKeys] = useState(new Set());
  const [deletedNotes, setDeletedNotes] = useState([]);
  const user = useSelector((state) => state.auth.user?.data);

  const filteredNotes = useMemo(() => {
    if (!claimDetails?.fbOcKey || !Array.isArray(claimNotes.data)) {
      return [];
    }

    return claimNotes.data.filter((note) => Number(note.fbOcKey) === Number(claimDetails.fbOcKey));
  }, [claimDetails?.fbOcKey, claimNotes.data]);

  const noteFormRefs = useRef(new Map());
  const notesListRef = useRef(notesList);
  const dirtyNoteKeysRef = useRef(dirtyNoteKeys);
  const filteredNotesRef = useRef(filteredNotes);
  const userRef = useRef(user);
  const lastClaimKeyRef = useRef(null);
  const deletedNotesRef = useRef(deletedNotes);

  useEffect(() => {
    notesListRef.current = notesList;
  }, [notesList]);

  useEffect(() => {
    dirtyNoteKeysRef.current = dirtyNoteKeys;
  }, [dirtyNoteKeys]);

  useEffect(() => {
    filteredNotesRef.current = filteredNotes;
  }, [filteredNotes]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    deletedNotesRef.current = deletedNotes;
  }, [deletedNotes]);

  const markNoteDirty = (noteKey) => {
    setDirtyNoteKeys((prev) => new Set(prev).add(noteKey));
  };

  const registerNoteForm = (noteKey, formInstance) => {
    if (formInstance) {
      noteFormRefs.current.set(noteKey, formInstance);
    } else {
      noteFormRefs.current.delete(noteKey);
    }
  };

  useImperativeHandle(ref, () => ({
    getNotesData: () => {
      const originalNotes = filteredNotesRef.current || [];
      const originalNotesByKey = new Map(originalNotes.map((note) => [getNoteKey(note), note]));
      const originalNoteKeys = new Set(originalNotes.map((note) => getNoteKey(note)));
      const deletedNoteKeys = new Set(deletedNotesRef.current.map((note) => getNoteKey(note)).filter((key) => key));

      const notesToProcess =
        notesListRef.current.length > 0
          ? notesListRef.current
          : originalNotes
              .filter((note) => {
                const noteKey = getNoteKey(note);
                return !deletedNoteKeys.has(noteKey);
              })
              .map((note) => ({ ...note, editMode: true, isNew: false }));

      const activeNotes = notesToProcess.map((note) => {
        const noteKey = getNoteKey(note);
        const form = noteFormRefs.current.get(noteKey);
        const formData = form?.getFieldsValue() ?? note;
        const originalNote = originalNotesByKey.get(noteKey);
        const currentCommentType = formData?.commentType ?? note.commentType;
        const previousCommentType = originalNote?.commentType ?? note.oldCommentType ?? null;

        const currentCommentText = (formData?.commentText ?? note.commentText ?? '').trim();
        const originalCommentText = (originalNote?.commentText ?? '').trim();
        const originalCommentType = originalNote?.commentType;

        const hasValueChanged =
          Boolean(originalNote) &&
          (currentCommentType !== originalCommentType || currentCommentText !== originalCommentText);

        let dirtyFlag = 'N';

        const existsInOriginal = originalNoteKeys.has(noteKey);
        const isDirty = dirtyNoteKeysRef.current.has(noteKey) || hasValueChanged;

        if (note.isNew) {
          dirtyFlag = existsInOriginal ? (isDirty ? 'U' : 'N') : 'I';
        } else if (existsInOriginal) {
          dirtyFlag = isDirty ? 'U' : 'N';
        } else if (isDirty) {
          dirtyFlag = 'U';
        }

        return {
          ...note,
          ...formData,
          ...(note.isNew &&
            !existsInOriginal && {
              createdByUser: `${userRef.current?.firstName ?? ''} ${userRef.current?.lastName ?? ''}`.trim(),
              createdOn: getLocalDateTime().toISOString(),
            }),
          commentType: currentCommentType,
          dirtyFlag,
          oldCommentType: note.isNew ? null : previousCommentType,
        };
      });

      const deletedNotesData = deletedNotesRef.current.map((note) => ({
        ...note,
        dirtyFlag: 'D',
      }));

      return [...activeNotes, ...deletedNotesData];
    },

    validateAllNotes: async () => {
      try {
        const forms = [...noteFormRefs.current.values()].filter(Boolean);
        if (forms.length === 0) {
          return Promise.resolve();
        }
        await Promise.all(
          forms.map((form) => {
            if (!form || typeof form.validateFields !== 'function') {
              return Promise.resolve();
            }
            return form.validateFields();
          }),
        );
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }));

  useEffect(() => {
    const currentFbOcKey = claimDetails?.fbOcKey;
    if (!currentFbOcKey) {
      return;
    }
    if (lastClaimKeyRef.current !== null && lastClaimKeyRef.current !== currentFbOcKey) {
      setNotesList([]);
      setDeletedNotes([]);
      setDirtyNoteKeys(new Set());
      noteFormRefs.current.clear();
    }
    lastClaimKeyRef.current = currentFbOcKey;
  }, [claimDetails?.fbOcKey]);

  useEffect(() => {
    if (!isSuccess || claimNotes.data === undefined) {
      return;
    }
    const deletedNoteKeys = new Set(deletedNotesRef.current.map((note) => getNoteKey(note)).filter((key) => key));
    setNotesList(
      claimNotes.data
        .filter((data) => {
          const noteKey = getNoteKey(data);
          return !deletedNoteKeys.has(noteKey);
        })
        .map((data) => ({
          ...data,
          editMode: true,
          isNew: false,
        })),
    );
    setDirtyNoteKeys(new Set());
    setDeletedNotes([]);
  }, [claimNotes.data, isSuccess]);

  const { handleNextPage, scrollHasMore } = useOCClaimScrollPagination({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loadedCount: claimNotes.data?.length ?? 0,
    totalRows: claimNotes.totalRows ?? 0,
  });

  const isNotesLoading = isLoading || (isFetching && !isFetchingNextPage);

  const handleSubmit = (_data, index) => {
    const note = notesList[index];
    if (note && !note.isNew) {
      markNoteDirty(getNoteKey(note));
    }
  };

  const handleDelete = (index) => {
    const noteToDelete = notesList[index];
    if (!noteToDelete) {
      return;
    }

    if (noteToDelete.isNew) {
      const remainingNotes = notesList.filter((_, i) => i !== index);
      setNotesList(remainingNotes);
      const noteKey = getNoteKey(noteToDelete);
      noteFormRefs.current.delete(noteKey);
    } else {
      const remainingNotes = notesList.filter((_, i) => i !== index);
      setNotesList(remainingNotes);
      const noteKey = getNoteKey(noteToDelete);
      noteFormRefs.current.delete(noteKey);
      setDeletedNotes((prev) => [...prev, noteToDelete]);
    }
  };

  const handleAddNew = () => {
    const allNotes = [...filteredNotes, ...notesList];
    const maxNote = maxBy(allNotes, (note) => Number(getNoteKey(note)));
    const nextSeq = (maxNote ? Number(getNoteKey(maxNote)) : 0) + 1;

    setNotesList([
      {
        commentText: undefined,
        commentType: undefined,
        dirtyFlag: 'i',
        editMode: true,
        isNew: true,
        oldCommentType: null,
        sequenceNo: nextSeq,
      },
      ...notesList,
    ]);
  };

  return (
    <div className="d-flex  py-16 h-100" style={{ flexDirection: 'column' }}>
      <Row className="px-30 align-items-center" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">{t('translation:claims.overchargeClaimNotes')}</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={handleAddNew}>{t('translation:claims.addNewNote')}</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100 " />
      {notesList.length > 0 || isNotesLoading ? (
        <ScrollPagination
          className="px-30 py-10 flex-1"
          loadMore={handleNextPage}
          isLoading={isFetchingNextPage}
          hasMore={scrollHasMore}
          scrollOffset={30}
          disabledForceNextPage
          isSkipScrollToBottom
        >
          {notesList.map((row, index) => (
            <OverChargeNoteCard
              noteDetails={row}
              key={getNoteKey(row)}
              index={index}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
              onFieldChange={() => markNoteDirty(getNoteKey(row))}
              registerForm={registerNoteForm}
            />
          ))}
        </ScrollPagination>
      ) : (
        <EmptyRecords />
      )}
    </div>
  );
});

export default OverChargeNotes;
